import React from "react";
import {Formik, Form, Field} from "formik";
import * as Yup from "yup";
import Modal from "react-modal";
import "./proposalStyle.css";
import {emailRegex, phoneRegex} from "../../../utils/constants";
import {
  combineFrenchDateAndTime,
  parseFrenchDate,
} from "../../../utils/date";
import {
  FrenchDateField,
  FrenchTimeField,
} from "../shared/FrenchDateTimeFields";

const ProposalSchema = Yup.object().shape({
  title: Yup.string()
    .required("Titre est requis")
    .test(
      "no-phone-or-email",
      "Le titre ne doit pas contenir de numéro de téléphone ou d'adresse e-mail",
      (value) =>
        !value || (!emailRegex.test(value) && !phoneRegex.test(value)),
    ),
  appointmentDate: Yup.string()
    .required("La date du RDV est requise")
    .test(
      "is-valid-date",
      "Date du RDV invalide (format attendu : jj/mm/aaaa)",
      (value) => Boolean(parseFrenchDate(value)),
    ),
  appointmentTime: Yup.string()
    .matches(
      /^([01]\d|2[0-3]):([0-5]\d)$/,
      "L'heure doit être au format 24 h (HH:mm)",
    )
    .required("L'heure est requise")
    .test(
      "is-future-time",
      "La date et l'heure du RDV doivent être ultérieures à maintenant",
      function (value) {
        const appointment = combineFrenchDateAndTime(
          this.parent.appointmentDate,
          value,
        );
        return !appointment || appointment.getTime() > Date.now();
      },
    ),
  initialPrice: Yup.number()
    .typeError("Prix initial doit être un nombre")
    .required("Prix initial est requis")
    .min(1, "Le prix proposé doit être supérieur ou égal à 1 €"),
  comment: Yup.string()
    .optional()
    .test(
      "no-phone-or-email",
      "Le commentaire ne doit pas contenir de numéro de téléphone ou d'adresse e-mail",
      (value) =>
        !value || (!emailRegex.test(value) && !phoneRegex.test(value)),
    ),
});

const modalStyles = {
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  content: {
    transform: "translateY(30px)",
    position: "relative",
    width: "90%",
    maxWidth: "554px",
    maxHeight: "80vh",
    margin: "auto",
    borderRadius: "12px",
    padding: "0px",
    overflow: "auto",
  },
};

const ProposalModal = ({isOpen, onClose, onSubmit, initialValues}) => (
  <Modal
    className="h-fit bg-white z-999 !p-6 xl:!p-12"
    isOpen={isOpen}
    onRequestClose={onClose}
    style={modalStyles}
  >
    <div className="flex flex-col justify-start w-full h-fit">
      <div className="text-[26px] leading-6 text-[#FF6600] w-full text-center font-bold mb-3 xl:mb-5">
        Faire une proposition
      </div>
      <Formik
        enableReinitialize
        initialValues={{...initialValues}}
        validationSchema={ProposalSchema}
        onSubmit={(values, {resetForm}) => {
          onSubmit(values);
          resetForm();
          onClose();
        }}
      >
        {({errors, touched}) => (
          <Form className="grid grid-cols-2 gap-2 xl:gap-4">
            <div className="col-span-2">
              <label className="block font-bold text-sm text-[#3D4465]">
                Titre de l'intervention <span className="text-red-500">*</span>
              </label>
              <p className="mb-1 text-xs leading-4 text-[#7E7E7E]">
                Ce titre sera affiché sur la facture. Exemple : Remplacement
                d'une serrure de porte.
              </p>
              <Field
                name="title"
                aria-required="true"
                className="w-full px-3 py-2 border !rounded-lg"
              />
              {errors.title && touched.title && (
                <div className="text-red-500 text-sm">{errors.title}</div>
              )}
            </div>

            <div className="col-span-2">
              <label className="block font-bold text-sm text-[#3D4465]">
                Date du RDV <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-4 flex-wrap gap-y-2">
                <FrenchDateField
                  name="appointmentDate"
                  aria-required="true"
                  className="px-3 py-2 border rounded-md"
                />
                <FrenchTimeField
                  name="appointmentTime"
                  aria-required="true"
                  className="px-3 py-2 border rounded-md"
                />
              </div>
              {((errors.appointmentDate && touched.appointmentDate) ||
                (errors.appointmentTime && touched.appointmentTime)) && (
                <div className="text-red-500 text-sm">
                  {errors.appointmentDate || errors.appointmentTime}
                </div>
              )}
            </div>

            <div>
              <label className="block font-bold text-sm text-[#3D4465]">
                Prix proposé <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center w-full px-3 py-2 border rounded-md bg-white">
                <Field
                  name="initialPrice"
                  type="number"
                  min="1"
                  step="1"
                  aria-required="true"
                  className="border-none w-[90%]"
                />
                <span className="text-[#1A64B9]">€</span>
              </div>
              {errors.initialPrice && touched.initialPrice && (
                <div className="text-red-500 text-sm">
                  {errors.initialPrice}
                </div>
              )}
            </div>

            <div className="col-span-2">
              <label className="block font-bold text-sm text-[#3D4465]">
                Commentaire (optionnel)
              </label>
              <Field
                name="comment"
                as="textarea"
                rows="3"
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>

            <div className="flex col-span-2 justify-center w-full !mt-3 gap-4">
              <button
                onClick={onClose}
                type="button"
                className="w-1/2 text-sm flex justify-center items-center !py-3 px-2 min-w-[100px] !rounded-lg bg-white !text-[#0D44B1] !border-[#0D44B1] !border border-solid"
              >
                Annuler
              </button>
              <button
                type="submit"
                data-testid="proposal-submit"
                className="w-1/2 text-sm flex justify-center items-center py-3 px-2 min-w-[100px] !rounded-lg bg-gradient-to-r from-[#0022AA] to-[#1A64B9] text-white"
              >
                Envoyer
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  </Modal>
);

export default ProposalModal;
